import React, { useMemo } from "react";
import { useQuery } from "urql";
import { round, sumBy } from "lodash";

import { Grid, Column } from "../../../../components/src";
import { GET_FONAV_FUND_NAV, GET_FONAV_POSITIONS } from "./queries";
import {
    FoNavModelTypeSummaryRow,
    deltaPerNav,
    foNavNumberFormat,
    groupPositionsByModelType,
    sumNavAfterAdjustmentsAfterOrders,
    withDeltaPerNav
} from "./utils";

const deltaFormat = foNavNumberFormat("# ##0");
const deltaPerNavFormat = foNavNumberFormat("# ##0.00");

interface FoNavPositionRiskProps {
    fundId: string;
    startDate: string;
    endDate: string;
}

export function FoNavPositionRisk({ fundId, startDate, endDate }: FoNavPositionRiskProps): React.ReactElement {
    const [{ fetching: positionsFetching, error: positionsError, data: positionsData }] = useQuery({
        query: GET_FONAV_POSITIONS,
        variables: {
            fundId,
            date: endDate
        },
        pause: !fundId || !endDate,
        requestPolicy: "network-only"
    });

    const [{ fetching: navFetching, error: navError, data: navData }] = useQuery({
        query: GET_FONAV_FUND_NAV,
        variables: {
            fundId,
            date: startDate
        },
        pause: !fundId || !startDate,
        requestPolicy: "network-only"
    });

    const totalNav = useMemo(() => {
        return sumNavAfterAdjustmentsAfterOrders(navData?.previousNav?.[0]?.data?.classes);
    }, [navData]);

    const tableRows = useMemo((): FoNavModelTypeSummaryRow[] => {
        const rows = groupPositionsByModelType(positionsData?.positions);
        if (rows.length === 0) {
            return [];
        }

        const rowsWithPerNav = withDeltaPerNav(rows, totalNav);
        const sumRow: FoNavModelTypeSummaryRow = {
            modelType: "Sum",
            positionCount: sumBy(rows, (row) => row.positionCount),
            creditYieldDelta: sumBy(rows, (row) => row.creditYieldDelta),
            interestRateYieldDelta: sumBy(rows, (row) => row.interestRateYieldDelta),
            creditDeltaPerNav: deltaPerNav(sumBy(rows, (row) => row.creditYieldDelta), totalNav),
            yieldDeltaPerNav: deltaPerNav(sumBy(rows, (row) => row.interestRateYieldDelta), totalNav)
        };

        return [...rowsWithPerNav, sumRow];
    }, [positionsData, totalNav]);

    const fetching = positionsFetching || navFetching;
    const error = positionsError || navError;

    if (fetching) {
        return <p>Loading position risk...</p>;
    }

    if (error) {
        return <p>error: {JSON.stringify(error, null, 2)}</p>;
    }

    if (tableRows.length === 0) {
        return <p>No positions found for {endDate}.</p>;
    }

    return (
        <Grid header="Position risk by model type" data={tableRows} className="table-timeseries" tableClassName="table-xs" sortable>
            <Column field="modelType" title="Model type" className="nowrap left" />
            <Column field="positionCount" title="Positions" />
            <Column
                field="creditYieldDelta"
                title="Credit Delta"
                className="nowrap left"
                format={(value: number) => deltaFormat(round(value, 0))}
            />
            <Column
                field="creditDeltaPerNav"
                title="Credit risk"
                className="nowrap left"
                format={(value: number | null) => {
                    if (value === null || typeof value === "undefined") {
                        return "-";
                    }
                    return deltaPerNavFormat(round(value, 2));
                }}
            />
            <Column
                field="interestRateYieldDelta"
                title="Yield Delta"
                className="nowrap left"
                format={(value: number) => deltaFormat(round(value, 0))}
            />
            <Column
                field="yieldDeltaPerNav"
                title="Rate risk"
                className="nowrap left"
                format={(value: number | null) => {
                    if (value === null || typeof value === "undefined") {
                        return "-";
                    }
                    return deltaPerNavFormat(round(value, 2));
                }}
            />
        </Grid>
    );
}
